import React, { memo } from 'react'
import clsx from 'clsx'
import styles from './EntityMultiSelect.module.css'
import Checkbox from '../../controls/Checkbox/Checkbox'

interface Props {
  id: number
  index: number
  label: string
  checked: boolean
  marked: boolean
  active: boolean
  disabled?: boolean
  showMark: boolean
  className?: string
  onToggle: (id: number) => void
  onToggleMark: (id: number) => void
  onFocus: (index: number) => void
  renderMarkIcon?: (marked: boolean) => React.ReactNode
}

/**
 * Deliberately non-generic and memoized: it only receives primitives plus stable callbacks, so
 * ticking one row re-renders that row instead of the whole list.
 */
export const EntityMultiSelectRow = memo(function EntityMultiSelectRow({
  id,
  index,
  label,
  checked,
  marked,
  active,
  disabled,
  showMark,
  className,
  onToggle,
  onToggleMark,
  onFocus,
  renderMarkIcon,
}: Props) {
  return (
    <div
      data-index={index}
      role="option"
      aria-selected={checked}
      aria-disabled={disabled || undefined}
      tabIndex={active ? 0 : -1}
      onFocus={() => onFocus(index)}
      onClick={() => onToggle(id)}
      className={clsx(
        styles.row,
        checked && styles.rowSelected,
        disabled && styles.rowDisabled,
        className,
      )}
    >
      <div className={styles.rowLeft}>
        <span className={clsx(styles.label, checked && styles.labelSelected)}>
          {label}
        </span>

        {showMark && (
          <span
            role="button"
            aria-label={marked ? 'Markierung entfernen' : 'Markieren'}
            aria-pressed={marked}
            onClick={(event) => {
              event.stopPropagation()
              onToggleMark(id)
            }}
            className={clsx(styles.markIcon, marked && styles.markIconActive)}
          >
            {renderMarkIcon ? renderMarkIcon(marked) : <span aria-hidden>★</span>}
          </span>
        )}
      </div>

      {/* Indicator only — the row is the control. It is a `role="option"` carrying `aria-selected`
          and the roving `tabIndex`, so a real checkbox in here would be a second, invalid tab stop
          for the same choice. Without the old `stopPropagation` wrapper a click on the tick mark
          falls through to the row's own `onToggle`, which is what it called anyway. */}
      <div className={clsx(styles.checkboxWrap, checked && styles.checkboxWrapSelected)}>
        <Checkbox checked={checked} />
      </div>
    </div>
  )
})
